import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  CheckCircle2,
  Calendar,
  MapPin,
  Phone,
  MessageCircle,
  Instagram,
  Flag,
  ShieldAlert,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { joinEvent, reportEvent } from "@/lib/mapioApi";
import ChatPanel from "@/components/ChatPanel";

const panelVariants = {
  hidden: {
    y: 28,
    x: 28,
    opacity: 0,
    scale: 0.98,
  },
  visible: {
    y: 0,
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: {
    y: 20,
    x: 18,
    opacity: 0,
    scale: 0.98,
  },
};

const panelTransition = {
  type: "tween",
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1],
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function EventDetailPanel({ event, onClose }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [requested, setRequested] = useState(false);
  const isDisco = event?.category === "Disco";

  useEffect(() => {
    setPhotoIdx(0);
    setShowChat(false);
    setRequested(false);
  }, [event?.id]);

  const handleRequestInvite = async () => {
    if (!event) return;
    try {
      await joinEvent(event.id, "Guest User");
      setRequested(true);
      toast.success("Invite requested. Host will be in touch.");
    } catch {
      toast.error("Could not send request. Try again.");
    }
  };

  const handleReport = async () => {
    if (!event) return;
    try {
      await reportEvent(event.id);
      toast.success("Reported. Our team will review.");
    } catch {
      toast.error("Could not submit report.");
    }
  };

  const photos = event?.photos?.length ? event.photos : event ? [event.cover_image] : [];

  return (
    <AnimatePresence>
      {event && (
        <>
          {/* Backdrop on mobile only */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[600] bg-black/10 sm:hidden"
          />
          <motion.aside
            data-testid="event-detail-panel"
            data-event-theme={isDisco ? "disco" : "default"}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={panelTransition}
            className="event-detail-panel fixed z-[700] bg-white border border-black/5 shadow-[0_16px_64px_rgba(0,0,0,0.18)] flex flex-col overflow-hidden will-change-transform
                       bottom-0 left-0 right-0 max-h-[85vh] rounded-t-[28px]
                       sm:bottom-6 sm:top-6 sm:right-6 sm:left-auto sm:w-[420px] sm:max-h-none sm:rounded-[24px]"
          >
            {/* Cover image */}
            <div className="event-detail-cover relative h-56 shrink-0 bg-gray-100">
              {photos[photoIdx] && (
                <img
                  src={photos[photoIdx]}
                  alt={event.name}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              )}
              <button
                data-testid="close-detail-btn"
                onClick={onClose}
                className="event-detail-close absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md hover:bg-white transition"
              >
                <X className="w-4 h-4 text-gray-800" />
              </button>
              {photos.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {photos.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPhotoIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === photoIdx ? "w-6 bg-white" : "w-1.5 bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              )}
              {event.age_restriction && (
                <span className="absolute top-3 left-3 text-[11px] font-semibold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full border border-amber-200">
                  18+ Age Restricted
                </span>
              )}
            </div>

            {/* Content */}
            <div className="event-detail-content flex-1 overflow-y-auto px-5 pt-5 pb-32">
              <div className="flex items-start gap-3 mb-1">
                <h2 data-testid="event-name" className="event-detail-title text-xl font-bold text-gray-900 leading-tight" style={{ fontFamily: "Manrope" }}>
                  {event.name}
                </h2>
              </div>

              <div className="event-detail-host flex items-center gap-2 mb-4 text-sm">
                <span className="text-gray-700 font-medium">{event.host_name}</span>
                <CheckCircle2 className="event-detail-verified w-4 h-4 text-blue-600 fill-blue-100" />
                <span className="text-xs text-gray-500">Verified host</span>
              </div>

              <div className="event-detail-ticket inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 text-sm font-semibold text-blue-700 mb-5">
                Ticket: {event.ticket_price}
              </div>

              <div className="event-detail-meta grid grid-cols-1 gap-3 mb-5">
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <Calendar className="event-detail-meta-icon w-4 h-4 text-gray-500 shrink-0" />
                  <span>
                    {formatDate(event.event_date)} • {event.event_time}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <MapPin className="event-detail-meta-icon w-4 h-4 text-gray-500 shrink-0" />
                  <span>{event.location_area}</span>
                </div>
                {event.members?.length > 0 && (
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Users className="event-detail-meta-icon w-4 h-4 text-gray-500 shrink-0" />
                    <span>{event.members.length} going</span>
                  </div>
                )}
              </div>

              {event.description && (
                <p className="event-detail-description text-sm text-gray-600 leading-relaxed mb-5">
                  {event.description}
                </p>
              )}

              {/* Photo grid (additional) */}
              {photos.length > 1 && (
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {photos.slice(0, 6).map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setPhotoIdx(i)}
                      className={`event-detail-thumb aspect-square overflow-hidden rounded-xl border-2 transition ${
                        i === photoIdx ? "border-blue-600" : "border-transparent"
                      }`}
                    >
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Host contact */}
              <div className="event-detail-contact rounded-2xl bg-gray-50 border border-gray-100 p-4 mb-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Contact Host
                </p>
                <div className="flex items-center gap-2">
                  {event.host_whatsapp && (
                    <a
                      data-testid="contact-whatsapp"
                      href={`https://wa.me/${event.host_whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="event-detail-contact-btn flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-white border border-gray-200 hover:border-green-500 hover:text-green-600 transition text-sm font-medium"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </a>
                  )}
                  {event.host_phone && (
                    <a
                      data-testid="contact-call"
                      href={`tel:${event.host_phone}`}
                      className="event-detail-contact-btn flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-white border border-gray-200 hover:border-blue-500 hover:text-blue-600 transition text-sm font-medium"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </a>
                  )}
                  {event.host_instagram && (
                    <a
                      data-testid="contact-instagram"
                      href={`https://instagram.com/${event.host_instagram.replace("@", "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="event-detail-contact-btn flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-white border border-gray-200 hover:border-pink-500 hover:text-pink-600 transition text-sm font-medium"
                    >
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </a>
                  )}
                </div>
              </div>

              {/* Safety / disclaimer */}
              <div className="event-detail-safety text-xs text-gray-500 flex items-start gap-2 mb-1">
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Mapix does not process payments. Contact hosts directly. Exact addresses are shared after invite.
                </span>
              </div>
              {event.safety_note && (
                <p className="event-detail-safety-note text-xs text-gray-500 italic">Note: {event.safety_note}</p>
              )}

              <button
                data-testid="report-event-btn"
                onClick={handleReport}
                className="event-detail-report mt-4 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 transition"
              >
                <Flag className="w-3.5 h-3.5" />
                Report this event
              </button>
            </div>

            {/* Sticky CTAs */}
            <div className="event-detail-cta absolute bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-xl border-t border-black/5 flex gap-2">
              <button
                data-testid="request-invite-btn"
                onClick={handleRequestInvite}
                disabled={requested}
                className="event-detail-primary flex-1 py-3 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-sm shadow-md hover:shadow-lg transition disabled:opacity-60"
              >
                {requested ? "Invite Requested" : "Request Invite"}
              </button>
              <button
                data-testid="join-chat-btn"
                onClick={() => setShowChat(true)}
                className="event-detail-secondary flex-1 py-3 rounded-full bg-gray-100 text-gray-900 font-semibold text-sm hover:bg-gray-200 transition"
              >
                Join Chat
              </button>
            </div>
          </motion.aside>

          {showChat && (
            <ChatPanel event={event} onClose={() => setShowChat(false)} />
          )}
        </>
      )}
    </AnimatePresence>
  );
}
